import type { BudgetCategoryKey } from "@/features/event-details/_types/ui-types";
import type { BudgetItemField, CreateEventFormData, FoodBeverageMeal, StepConfig } from "../_types/wizard-types";
import type { MeetingSpaceSchedule } from "@/lib/database/events";
export { EVENT_SOURCE_OPTIONS, EVENT_SOURCE_OTHER_VALUE } from "@/lib/events/event-source-options";

/** Maximum character length for event names. */
export const NAME_MAX_LENGTH = 64;

/**
 * Query-parameter key that signals a wizard resume after the auth roundtrip.
 * Kept here so the writer (VenuePreviewResults submit handler) and the reader
 * (CreateEventWizard mount) can't drift.
 */
export const RESUME_QUERY_PARAM = "resume";

/** Expected value of `RESUME_QUERY_PARAM` when resuming the wizard. */
export const RESUME_QUERY_VALUE = "1";

/** Path the wizard returns to with resume signaling appended. */
export const WIZARD_RESUME_PATH = `/create?${RESUME_QUERY_PARAM}=${RESUME_QUERY_VALUE}`;

/** Number of venue results to show in the final results view. */
export const RESULTS_LIMIT = 5;

/** Debounce window before a draft field change autosaves. Matches `event-details`'
 *  `AUTO_SAVE_DELAY_MS` — long enough to coalesce typing, short enough to feel responsive. */
export const AUTOSAVE_DELAY_MS = 1500;

/** Minimum time (ms) each of `SearchProgress`'s first two steps stays visible before advancing —
 *  pure pacing so the sequence doesn't flash by unreadably fast, not tied to the real search. */
export const SEARCH_PROGRESS_MIN_STEP_DURATION = 500;
/** Minimum time (ms) `SearchProgress`'s final step stays visible after the real search resolves,
 *  before calling `onComplete` — gated on the fetch instead of a fixed wall-clock wait. */
export const SEARCH_PROGRESS_FINAL_STEP_MIN_DURATION = 400;

/** Below this venue budget, non-admin users see the "not the right fit" warning modal on blur. */
export const LOW_BUDGET_THRESHOLD = 20_000;

/**
 * The wizard field that holds each `BUDGET_CATEGORIES` amount. Typed over every category key, so a
 * category added there fails typecheck here until the wizard asks for it too.
 */
export const BUDGET_CATEGORY_FIELDS = {
  venue: "budgetVenue",
  fb: "budgetFoodBeverage",
  av: "budgetAv",
  transport: "budgetTransportation",
  flight: "budgetFlights",
  activities: "budgetActivities",
  merch: "budgetMerch",
  other: "budgetOther",
} as const satisfies Record<BudgetCategoryKey, "budgetVenue" | BudgetItemField>;

/** Max locations selectable on the location step — the autocomplete and suggestion pills share this. */
export const LOCATION_LIMIT = 5;

/** Minimum hotel rooms per night required to source through the platform. */
export const MIN_ROOMS_PER_NIGHT = 10;

/**
 * Whether a chosen max-rooms-per-night is below the platform minimum and should
 * block progression past the guestrooms step.
 * @param count - Max rooms per night the user entered.
 * @returns `true` if `count` is below {@link MIN_ROOMS_PER_NIGHT}.
 */
export function isBelowRoomMinimum(count: number): boolean {
  return count < MIN_ROOMS_PER_NIGHT;
}

/** Most pre-nights (or post-nights) the guestrooms grid lets a planner add on each side of the
 *  program — enough for early staff arrivals or a late checkout, small enough to stay a shoulder. */
export const MAX_SHOULDER_NIGHTS = 3;

/** Guestrooms grid columns that aren't room types: the night label and the nightly total. */
export const GUESTROOM_GRID_FIXED_COLUMNS = 2;

/** Per-IP rate-limit window for the public venue-preview search server action. */
export const PREVIEW_SEARCH_RATE_LIMIT_WINDOW_MS = 60_000;

/** Per-IP max calls per window for the public venue-preview search server action (proxies a privileged,
 *  billed internal API with no auth gate). */
export const PREVIEW_SEARCH_RATE_LIMIT_MAX = 30;

/** Max payloads (locations) accepted per `searchVenuePreviews` call — matches {@link MAX_PREFILL_CITIES},
 *  the location step's own selection limit. Without this, one call could batch far more than a legitimate
 *  search ever needs, fanning out to unlimited billed upstream fetches while the rate limiter (checked
 *  once per call, not per payload) only sees a single hit. */
export const MAX_PREVIEW_SEARCH_PAYLOADS = 5;

/** Per-IP rate-limit window for the public Northstar-request lookup server action. */
export const NORTHSTAR_REQUEST_RATE_LIMIT_WINDOW_MS = 60_000;

/** Per-IP max calls per window for the public Northstar-request lookup (unauthenticated, service-role
 *  read keyed only by an unguessable uuid — bounds it from becoming an unrate-limited enumeration oracle
 *  if a link leaks). */
export const NORTHSTAR_REQUEST_RATE_LIMIT_MAX = 30;

/** A clickable suggested-location pill: city-only `label` for display, `query` to disambiguate the
 *  Google Places lookup. */
export interface SuggestedLocation {
  label: string;
  query: string;
}

/**
 * Curated top event cities shown as one-click suggestion pills on the location step. Derived from a
 * one-off aggregation of `nowadays.events.formatted_locations` (top cities by event count); kept static
 * to avoid a runtime query. Refresh by re-running that aggregation. `query` includes the state so the
 * region autocomplete resolves the intended city.
 */
export const SUGGESTED_LOCATIONS: SuggestedLocation[] = [
  { label: "San Francisco", query: "San Francisco, CA" },
  { label: "New York", query: "New York, NY" },
  { label: "Chicago", query: "Chicago, IL" },
  { label: "Atlanta", query: "Atlanta, GA" },
  { label: "Austin", query: "Austin, TX" },
];

/** Office pills shown on the location step before the rest collapse behind "+N more" — matches the
 *  number of popular-city pills, so a large enterprise's office list can't push Continue off screen. */
export const VISIBLE_OFFICE_SUGGESTIONS = 5;

/** Sentinel `place_id` prefix for an optimistic (not-yet-resolved) location added from a suggestion pill.
 *  Real Google place ids never collide with this, so it uniquely identifies the placeholder to reconcile. */
export const OPTIMISTIC_PLACE_ID_PREFIX = "optimistic:";

/**
 * Query params for a Northstar partner redirect into `/create`. Shared between the writer
 * (`/create-northstar-event` resolver) and the reader (`CreateEventWizard` mount) so they can't drift.
 */
export const NORTHSTAR_QUERY_PARAM = "northstar";

/** Expected value of `NORTHSTAR_QUERY_PARAM` for an active Northstar redirect. */
export const NORTHSTAR_QUERY_VALUE = "1";

/** Query param carrying the opaque `draft_events.uuid`. */
export const NORTHSTAR_ID_PARAM = "ns_id";

/**
 * Query param signaling a deliberate "start fresh" entry into `/create` (e.g. the "New event" option
 * in `CreateEventModal`, as opposed to a bare `/create` visit). When present, `page.tsx` skips fetching
 * the user's most recent open draft, so a half-finished draft from a previous session never silently
 * resumes into a flow the user explicitly asked to start empty. The old draft itself is left alone —
 * this only affects whether THIS visit resumes it, not whether it still exists.
 */
export const CREATE_EVENT_FRESH_START_PARAM = "fresh";

/**
 * Query param carrying `enterprise_organizations.id` when a Nowadays admin launches `/create` from
 * admin-dash's org "Create event" button (ENG-918), to create the event for that org rather than the
 * admin's own account. Read and re-authorized server-side in `resolveAdminOrgParamResolution` —
 * never trusted client-side, since anyone can append this param to their own `/create` visit.
 */
export const ADMIN_ORG_ID_PARAM = "admin_org_id";

/**
 * Query param carrying `nowadays.users.user_id` when a Nowadays admin picks the user an
 * admin-launched event should belong to, via the mandatory owner-picker gate ahead of `/create`'s
 * wizard. Read and re-authorized server-side in `resolveAdminCreateEntryDecision`, never trusted
 * client-side, mirroring {@link ADMIN_ORG_ID_PARAM}.
 */
export const ADMIN_OWNER_ID_PARAM = "admin_owner_id";

/**
 * Query param marking a `/create` visit as launched from an admin-dash entry point (`OrgNav`'s or
 * `AdminAllEventsPage`'s "Create event" button) even when no org is pre-selected yet (an org-less
 * click still needs to land on the org-picker step, not skip it). Set unconditionally by every
 * admin-dash "Create event" button, alongside {@link ADMIN_ORG_ID_PARAM} when an org is already
 * known.
 *
 * This is the signal that distinguishes an admin-initiated launch from a Nowadays admin simply
 * using the product as themselves — a bare `/create` visit (a typed URL, or the CUSTOMER
 * dashboard's own "Create event" button) must never show the org/owner picker gate just because
 * the visitor happens to be staff; it should look exactly like a normal customer creating their
 * own event. `resolveAdminCreateEntryDecision` only evaluates the gate at all when this param, or
 * {@link ADMIN_ORG_ID_PARAM}/{@link ADMIN_OWNER_ID_PARAM}, is present. Like those params, this is a
 * routing hint only, never trusted for authorization on its own — `getCachedIsNowadaysAdmin()`
 * still gates everything the gate itself does.
 */
export const ADMIN_LAUNCH_PARAM = "admin_launch";

/**
 * Query param carrying the admin-dash page (pathname + search) a "Create event" button was clicked
 * from, so the admin entry gate's "Back to admin dashboard" link returns there instead of the
 * dashboard root. Untrusted: `/create` only honours it through `resolveAdminReturnPath`, which
 * accepts internal admin-dash paths only.
 */
export const ADMIN_RETURN_TO_PARAM = "return_to";

/** Defensive cap on how many requested venues a single Northstar request hydrates/persists. */
export const MAX_INJECTED_VENUES = 12;

/** Max distinct cities prefilled into the location step from a Northstar request's requested venues
 *  (matches the location step's selection limit). */
export const MAX_PREFILL_CITIES = 5;

/** All Northstar-requested venues are persisted as top-ranked candidates (mirrors the legacy flow). */
export const NORTHSTAR_CANDIDATE_RANK = 1;

/**
 * Ordered step configuration for the wizard. Dates sits right after Location (inside
 * "about-your-event") so headcount, guestrooms, and meeting rooms can all derive their dates from
 * the preferred event date rather than asking again — see `HeadcountStep`, `GuestroomsStep`, and
 * `MeetingRoomsStep`. "final-details" is terminal: completing it sets `isComplete` and the wizard
 * goes straight to venue-preview submission, exactly like v2's own terminal step ("flexibility") did.
 */
export const WIZARD_STEPS: StepConfig[] = [
  { id: "about-your-event", label: "About your event" },
  { id: "planning-doc", label: "Planning doc" },
  { id: "dates", label: "Dates" },
  { id: "headcount", label: "Headcount" },
  { id: "guestrooms", label: "Guestrooms" },
  { id: "meeting-rooms", label: "Meeting rooms" },
  { id: "food-beverage", label: "Food & beverage" },
  { id: "agenda", label: "Agenda" },
  { id: "budget-decision", label: "Budget & decision" },
  { id: "final-details", label: "Final details" },
];

/** Public, rate-limited route that runs the Reducto RFP extraction on an uploaded file. A route
 *  (not a server action) so the file bytes aren't subject to the server-action body cap. */
export const PLANNING_DOC_PARSE_ENDPOINT = "/api/rfp/parse";

/** File types the planning-doc step accepts: text-bearing documents the extractor can read. */
export const PLANNING_DOC_ACCEPT_ATTR = ".pdf,.doc,.docx,.txt";

/** Largest planning doc the parse route takes, in MB, kept under Vercel's ~4.5 MB function request-body cap. */
export const PLANNING_DOC_MAX_FILE_MB = 4;

/** {@link PLANNING_DOC_MAX_FILE_MB} in bytes. */
export const PLANNING_DOC_MAX_FILE_BYTES = PLANNING_DOC_MAX_FILE_MB * 1024 * 1024;

/** Typical end-to-end parse time; the progress bar eases toward its ceiling over this long. */
export const PLANNING_DOC_EXPECTED_PARSE_MS = 40_000;

/** How far the progress bar fills before the parse returns, so it never claims to be done early. */
export const PLANNING_DOC_PROGRESS_CEILING = 0.9;

/** Status lines shown in turn while a planning doc is parsed. `atMs` is when each stage becomes active; the
 *  extractor reports no progress, so these follow measured typical timings (first parse ~32 s). */
export const PLANNING_DOC_PARSE_STAGES = [
  { label: "Uploading…", atMs: 0 },
  { label: "Reading the pages…", atMs: 1_500 },
  { label: "Finding dates and locations…", atMs: 7_000 },
  { label: "Pulling out rooms, meals and budget…", atMs: 16_000 },
  { label: "Filling in your answers…", atMs: 26_000 },
] as const;

/** Wizard field → the label shown in the "we filled in" summary after a planning doc is parsed.
 *  Fields that belong to one question share a label; the summary dedupes them. */
export const PREFILL_FIELD_LABELS: Partial<Record<keyof CreateEventFormData, string>> = {
  name: "Event name",
  formattedLocations: "Location",
  numPeople: "Headcount",
  eventDates: "Dates",
  eventDatesAlternates: "Dates",
  flexibility: "Date flexibility",
  accommodations: "Guestrooms",
  accommodationsDetailed: "Guestrooms",
  meetingRooms: "Meeting rooms",
  foodBeverage: "Food & beverage",
  budgetVenue: "Budget",
  budgetFlights: "Budget",
  budgetFoodBeverage: "Budget",
  budgetTransportation: "Budget",
  budgetActivities: "Budget",
  budgetOther: "Budget",
  notesLocationBudget: "Budget notes",
  decisionDeadline: "Decision deadline",
  decisionFactors: "Decision factors",
  additionalServices: "Additional services",
  agendaItems: "Agenda",
  additionalNotes: "Notes",
};

/** The wizard's standard ease-out curve for small UI transitions. */
export const EASE_APP = [0.22, 1, 0.36, 1] as const;

/** A sharper ease-out for long progress fills. */
export const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

/** Animation variants for wizard-level step transitions. */
export const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -300 : 300,
    opacity: 0,
  }),
};

/** Transition config for wizard-level step animations. */
export const stepTransition = {
  type: "tween" as const,
  duration: 0.35,
  ease: "easeInOut" as const,
};

/** Animation variants for sub-phase transitions within a step. */
export const subPhaseVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 150 : -150,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -150 : 150,
    opacity: 0,
  }),
};

/** Staggered entrance variants for individual form elements. */
export const formElementVariants = {
  hidden: { opacity: 0 },
  visible: (i: number) => ({
    opacity: 1,
    transition: { delay: i * 0.08, duration: 0.35, ease: "easeOut" as const },
  }),
};

/**
 * Decision factors the wizard no longer offers. Not removed from `DECISION_FACTOR_OPTIONS`: that list
 * is also the stored-value allowlist for `events.decision_factors`, so events and drafts that already
 * hold one of these keep it (and `/details` still shows it).
 */
export const WIZARD_HIDDEN_DECISION_FACTORS: ReadonlySet<string> = new Set([
  "Capacity",
  "Safety and Security",
  "Venue Amenities",
]);

/** Venue hint in the itemized budget when a room block line is shown, so guest rooms aren't counted
 *  in both lines. */
export const ROOM_BLOCK_VENUE_HINT = "Meeting space and other venue costs";

/** A meeting space with no day or time given yet. */
export const EMPTY_MEETING_SPACE_SCHEDULE: MeetingSpaceSchedule = { days: [] };

/** Food & beverage services offered as pills, in display order. */
export const FOOD_BEVERAGE_MEAL_OPTIONS: readonly FoodBeverageMeal[] = ["Coffee & tea", "Breakfast", "Lunch", "Dinner"];

/** Times given to a seeded general-session agenda item when the planner set none. */
export const SEEDED_GENERAL_SESSION_TIMES = { startTime: "09:00", endTime: "17:00" } as const;

/** Times given to a seeded breakout agenda item when the planner set none. */
export const SEEDED_BREAKOUT_TIMES = { startTime: "13:00", endTime: "17:00" } as const;

/** Spacing between times in the meeting-space time picker. */
export const TIME_SLOT_STEP_MINUTES = 30;

/** Where the time picker scrolls to when nothing is picked yet: the start of a working day. */
export const TIME_PICKER_DEFAULT_SCROLL = "09:00";
